<template>
  <div class="bg-white border border-gray-200 rounded-lg p-4 h-full overflow-y-auto">
    <h2 class="text-base font-semibold text-gray-800 mb-1">Настройки рабочего стола СМК</h2>
    <p class="text-sm text-gray-600 mb-4">
      Ответственный за расследования несоответствий — сотрудник, на которого система заводит
      документы качества, создаваемые автоматически: расследование несоответствия по браку
      на производстве и коррекцию продукта по доработке на приёмке.
    </p>

    <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>

    <template v-else>
      <!-- Настройка пуста: без неё отклонить акт выпуска нельзя -->
      <div
        v-if="!responsibleUserId"
        class="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      >
        Ответственный не выбран. Пока он не выбран, отклонить акт о выпуске на этом рабочем столе нельзя.
      </div>

      <!-- Настройка заполнена, но сотрудника в системе больше нет -->
      <div
        v-else-if="isMissing"
        class="mb-4 rounded-lg border-2 border-red-400 bg-red-50 px-3 py-2 text-sm text-red-800"
      >
        Выбранный ранее сотрудник в системе больше не числится. Выберите ответственного заново,
        иначе отклонить акт о выпуске будет нельзя.
      </div>

      <div class="max-w-md">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Ответственный за расследования несоответствий
        </label>
        <v-select
          v-model="selectedUserId"
          :options="users"
          :reduce="user => user.id"
          :get-option-label="userName"
          placeholder="Выберите сотрудника"
        />
        <p class="text-xs text-gray-500 mt-1">
          В списке — сотрудники, у которых есть доступ к столу «Выпуск продукта».
        </p>
      </div>

      <div class="flex items-center gap-3 mt-4">
        <button
          v-can-write
          type="button"
          @click="save"
          :disabled="saving || !isDirty"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
        >
          {{ saving ? 'Сохранение...' : 'Сохранить' }}
        </button>
        <span v-if="savedUserName" class="text-sm text-gray-600">
          Сейчас назначен: {{ savedUserName }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * Настройка рабочего стола СМК «Ответственный за расследования несоответствий».
 *
 * Раньше строку настройки заводила миграция, а заполнить её было негде: из-за пустого
 * значения система молча не создавала расследования по браку. Здесь ответственный
 * выбирается из списка сотрудников стола, а не вводится идентификатором.
 */
export default {
  name: 'NcResponsibleSettings',
  props: {
    moduleId: { type: [String, Number], required: true },
  },
  data() {
    return {
      users: [],
      selectedUserId: null,
      responsibleUserId: null,
      savedUserName: '',
      isMissing: false,
      loading: false,
      saving: false,
    };
  },
  computed: {
    isDirty() {
      return this.selectedUserId !== this.responsibleUserId;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [settingResponse, usersResponse] = await Promise.all([
          axios.get('/api/smk/settings/nc-responsible', { params: { module_id: this.moduleId } }),
          axios.get('/api/smk/nc-investigation/users'),
        ]);

        if (usersResponse.data?.success) {
          this.users = usersResponse.data.data || [];
        }

        if (settingResponse.data?.success) {
          this.applySetting(settingResponse.data.data || {});
        } else {
          this.notifyError(null, settingResponse.data?.message || 'Не удалось загрузить настройку');
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось загрузить настройку');
      } finally {
        this.loading = false;
      }
    },
    applySetting(setting) {
      this.responsibleUserId = setting.user_id ?? null;
      this.selectedUserId = setting.user_id ?? null;
      this.savedUserName = setting.user_name || '';
      this.isMissing = !!setting.is_missing;
    },
    async save() {
      this.saving = true;
      try {
        const { data } = await axios.post('/api/smk/settings/nc-responsible', {
          module_id: this.moduleId,
          user_id: this.selectedUserId,
        });

        if (data?.success) {
          this.applySetting(data.data || {});
          this.notifySuccess(data.message || 'Ответственный сохранён');
        } else {
          this.notifyError(null, data?.message || 'Не удалось сохранить настройку');
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось сохранить настройку');
      } finally {
        this.saving = false;
      }
    },
    userName(user) {
      if (!user) return '';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || '';
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) {
        this.$root.$toast.success(message);
      }
    },
    notifyError(error, fallback) {
      const payload = error?.response?.data;
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      const msg = details.length
        ? details.join(' ')
        : (payload?.message || fallback || 'Ошибка');
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
